/**
 * Spaces types
 *
 * Central type definitions for Losi Spaces records, inserts, updates,
 * and shared view contracts.
 *
 * Primary responsibilities:
 * - Define persisted Space entities and helper unions
 * - Expose shared UI contracts for Space surfaces
 *
 * Dependencies:
 * - Supabase generated types
 */

import type { TLStoreSnapshot } from "tldraw"

import { Json, Tables } from "@/supabase/types"
import { StatusTemplate } from "@/types/view-templates"

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type SpaceRole = "owner" | "admin" | "editor" | "viewer" | "commenter"
export type SpaceVisibility = "private" | "workspace" | "public"
export type SpaceViewType =
  | "list"
  | "board"
  | "calendar"
  | "timeline"
  | "table"
  | "gantt"

export type SpaceDisplayPreferences = {
  showDayOverviewProgress?: boolean
  showItemsProgressOverview?: boolean
}
export type SpaceItemType =
  | "chat"
  | "file"
  | "collection"
  | "design"
  | "sheet"
  | "note"
  | "task"
  | "sprint"
  | "event"
  | "whiteboard"
  | "section"
  | "folder"
  | "conversation"

export type SpaceSelectableItemType =
  | "task"
  | "sheet"
  | "note"
  | "sprint"
  | "folder"
  | "conversation"

export type SpaceLinkedItemType = SpaceSelectableItemType | "design" | "whiteboard"

export type SpaceDesignSharing = "space" | "private" | "public"

export type ConversationContextType = "space" | "folder" | "sprint" | "task" | "note"

export type BuiltInTaskStatus =
  | "todo"
  | "in_progress"
  | "done"
  | "blocked"
  | "cancelled"

export type TaskStatus =
  | BuiltInTaskStatus
  | (string & {})

export type TaskPriority = "low" | "medium" | "high" | "urgent"

export type SprintStatus = "planned" | "active" | "completed" | "cancelled"

export type EventRSVPStatus = "pending" | "accepted" | "declined" | "tentative"

export type ActivityAction =
  | "created"
  | "updated"
  | "deleted"
  | "moved"
  | "assigned"
  | "completed"
  | "commented"
  | "shared"
  | "archived"
  | "restored"

export type ActivityEntityType =
  | "space"
  | "task"
  | "design"
  | "sheet"
  | "note"
  | "sprint"
  | "event"
  | "whiteboard"
  | "member"
  | "item"
  | "comment"

// ============================================================================
// BASE TYPES (from Supabase)
// ============================================================================

export type Space = Tables<"spaces">
export type SpaceMember = Tables<"space_members">
export type SpaceMemberWithProfile = SpaceMember & {
  email?: string | null
  display_name?: string | null
  image_url?: string | null
  username?: string | null
}
export type SpaceMemberCandidate = {
  user_id: string
  email?: string | null
  display_name?: string | null
  image_url?: string | null
  username?: string | null
  workspace_role?: string | null
}
export type SpaceMemberDirectory = {
  members: SpaceMemberWithProfile[]
  workspaceCandidates: SpaceMemberCandidate[]
  canManageMembers: boolean
  currentUserRole: SpaceRole | null
  sharing: "private" | "workspace" | "public"
  workspaceId: string | null
}
export type SpaceItem = Tables<"space_items">
export type SpaceDesign = Tables<"space_designs">
export type SpaceSheet = Tables<"space_sheets">
export type SpaceNote = Tables<"space_notes">
export type SpaceNoteComment = Tables<"space_note_comments">
export type SpaceNoteVersion = Tables<"space_note_versions">
export type SpaceTask = Tables<"space_tasks">
export type SpaceTaskAssigneeProfile = {
  id?: string | null
  user_id?: string | null
  display_name?: string | null
  username?: string | null
  email?: string | null
  image_url?: string | null
}
export type SpaceTaskWithAssignees = SpaceTask & {
  assignee_ids?: string[]
  assignee?: SpaceTaskAssigneeProfile | null
  assignees?: SpaceTaskAssigneeProfile[]
  assigner?: SpaceTaskAssigneeProfile | null
}
export type SpaceSprint = Tables<"space_sprints">
export type SpaceFolder = Tables<"space_folders">
export type SpaceTaskSubtask = Tables<"space_task_subtasks">
export type SpaceTaskChecklistItem = Tables<"space_task_checklist_items">
export type SpaceTaskComment = Tables<"space_task_comments">
export type SpaceTaskNote = Tables<"space_task_notes">
export type SpaceTaskAttachment = Tables<"space_task_attachments">
export type SpaceTaskVirtualFile = {
  id: string
  task_id: string
  user_id: string
  name: string
  path: string
  mime_type: string
  created_at: string
  updated_at: string | null
  task_title?: string | null
}
export type SpaceTaskLinkedItem = Tables<"space_task_linked_items">
// Shared Losi thread inside a Space. Any Space member with access can read
// and continue the conversation.
export type SpaceConversation = Tables<"space_conversations">
export type SpaceConversationMessage = Tables<"space_conversation_messages">
export type SpaceConversationMessageAuthor = Pick<
  Tables<"profiles">,
  "id" | "display_name" | "username" | "image_url"
>
export type SpaceConversationMessageWithAuthor = SpaceConversationMessage & {
  author?: SpaceConversationMessageAuthor | null
}
export type SpaceConversationContextCache = Tables<"space_conversation_context_cache">
export type SpaceConversationContextFile = Pick<
  Tables<"space_files">,
  "file_id" | "auto_include" | "context_priority"
> & {
  file?: Pick<
    Tables<"files">,
    "id" | "name" | "description" | "type" | "size" | "tokens" | "created_at"
  > | null
}
export type SpaceConversationContextFolderFile = Pick<
  Tables<"space_folder_files">,
  "file_id" | "auto_include" | "context_priority"
> & {
  file?: Pick<
    Tables<"files">,
    "id" | "name" | "description" | "type" | "size" | "tokens" | "created_at"
  > | null
}
export type SpaceConversationContextCollection = Pick<
  Tables<"space_collections">,
  "collection_id" | "auto_include" | "context_priority"
> & {
  collection?: Pick<
    Tables<"collections">,
    "id" | "name" | "description" | "created_at"
  > | null
}
export type SpaceConversationContextChat = Pick<
  Tables<"space_chats">,
  "chat_id" | "folder_id"
> & {
  position?: number | null
  chat?: Pick<
    Tables<"chats">,
    "id" | "name" | "created_at" | "updated_at" | "model" | "image_path"
  > | null
}
export type SpaceConversationSiblingFolder = Pick<
  SpaceFolder,
  "id" | "name" | "position"
> & {
  item_count: number
}
export type SpaceConversationMemory = Pick<
  Tables<"space_memories">,
  "id" | "memory_type" | "content" | "confidence" | "source_chat_id" | "created_at"
>
export type SpaceConversationContext = {
  type: ConversationContextType
  space_id: string
  timestamp: string
  hierarchy: string[]
  space?: Pick<
    Space,
    "id" | "name" | "description" | "custom_instructions" | "memory_isolation"
  > | null
  conversation?: SpaceConversation | null
  folder?: SpaceFolder | null
  sprint?: SpaceSprint | null
  task?: SpaceTask | null
  design?: SpaceDesign | null
  sheet?: SpaceSheet | null
  note?: SpaceNote | null
  folders?: SpaceFolder[]
  sprints?: SpaceSprint[]
  tasks?: SpaceTask[]
  designs?: SpaceDesign[]
  sheets?: SpaceSheet[]
  notes?: SpaceNote[]
  conversations?: SpaceConversation[]
  subtasks?: SpaceTaskSubtask[]
  checklist?: SpaceTaskChecklistItem[]
  task_comments?: SpaceTaskComment[]
  task_notes?: SpaceTaskNote[]
  attachments?: SpaceTaskAttachment[]
  linked_items?: SpaceTaskLinkedItem[]
  space_files?: SpaceConversationContextFile[]
  folder_files?: SpaceConversationContextFolderFile[]
  space_collections?: SpaceConversationContextCollection[]
  linked_chats?: SpaceConversationContextChat[]
  sibling_folders?: SpaceConversationSiblingFolder[]
  memories?: SpaceConversationMemory[]
}
export type TaskLinkedItemType =
  | "chat"
  | "company"
  | "contact"
  | "conversation"
  | "design"
  | "file"
  | "folder"
  | "note"
  | "sheet"
  | "sprint"
  | "task"
  | "whiteboard"

export type TaskAttachableItem = {
  item_id: string
  item_type: TaskLinkedItemType
  title: string
  subtitle?: string | null
  url: string
  source_space_id?: string | null
  updated_at?: string | null
}

export type SpaceEvent = Tables<"space_events">
export type SpaceCalendarFeedEvent = {
  id: string
  title: string
  description?: string
  start_date: string
  end_date?: string
  all_day?: boolean
  event_type: "meeting" | "deadline" | "milestone" | "reminder"
  color?: "emerald" | "sky" | "slate" | "amber"
  source: "space" | "google" | "apple"
  source_label: string
  read_only: boolean
  location?: string
  resource_names?: string[]
  task_id?: string | null
}
export type SpaceEventAttendee = Tables<"space_event_attendees">
export type SpaceWhiteboard = Tables<"space_whiteboards">
export type SpaceActivity = Tables<"space_activity">
export type SpaceView = Tables<"space_views">

export type SelectedSpaceItem =
  | {
      type: "task"
      id: string
      data: SpaceTask
    }
  | {
      type: "sheet"
      id: string
      data: SpaceSheet
    }
  | {
      type: "note"
      id: string
      data: SpaceNote
    }
  | {
      type: "sprint"
      id: string
      data: SpaceSprint
    }
  | {
      type: "folder"
      id: string
      data: SpaceFolder
    }
  | {
      type: "conversation"
      id: string
      data: SpaceConversation
    }
  | null

// ============================================================================
// INSERT TYPES
// ============================================================================

export type SpaceInsert = {
  user_id: string
  workspace_id: string
  folder_id?: string | null
  name: string
  description?: string | null
  icon?: string | null
  color?: string | null
  custom_instructions?: string | null
  memory_isolation?: "default" | "space_only" | null
  status_template?: StatusTemplate | null // JSONB column storing StatusTemplate object
  priority_template?: any | null // JSONB column storing PriorityTemplate object
  display_preferences?: SpaceDisplayPreferences | null
  default_view?: SpaceViewType
  sharing?: SpaceVisibility
  archived?: boolean
  favorited?: boolean
}

export type SpaceMemberInsert = {
  space_id: string
  user_id: string
  role?: SpaceRole
  added_by?: string | null
}

export type SpaceNoteInsert = {
  user_id: string
  space_id: string
  title: string
  content?: string | null
  content_type?: "markdown" | "html" | "json"
  sharing?: "space" | "private" | "public"
  locked?: boolean
  template?: boolean
  sprint_id?: string | null
  folder_id?: string | null
}

export type SpaceDesignInsert = {
  user_id: string
  space_id: string
  title: string
  description?: string | null
  design_data?: Json
  sharing?: SpaceDesignSharing
  locked?: boolean
  sprint_id?: string | null
  folder_id?: string | null
}

export type SpaceSheetInsert = {
  user_id: string
  space_id: string
  title: string
  description?: string | null
  workbook_data?: Json
  sharing?: "space" | "private" | "public"
  locked?: boolean
  sprint_id?: string | null
  folder_id?: string | null
}

export type SpaceTaskInsert = {
  user_id: string
  space_id: string
  sprint_id?: string | null
  folder_id?: string | null
  parent_task_id?: string | null
  title: string
  description?: string | null
  status?: TaskStatus
  priority?: TaskPriority
  assigned_to?: string | null
  assignee_ids?: string[] | null
  due_date?: string | null
  start_date?: string | null
  tags?: string[]
  estimated_hours?: number | null
  actual_hours?: number | null
  department?: string | null
  project?: string | null
  task_type?: string | null
  position?: number
  recurrence_rule?: Json | null
  recurrence_anchor_task_id?: string | null
  recurrence_parent_task_id?: string | null
  recurrence_instance_index?: number
  recurrence_completed_count?: number
  recurrence_next_at?: string | null
  recurrence_last_generated_at?: string | null
}

export type SpaceSprintInsert = {
  user_id: string
  space_id: string
  name: string
  description?: string | null
  start_date: string
  end_date: string
  status?: SprintStatus
  goal?: string | null
  capacity_hours?: number | null
  sprint_duration?: number
  parent_sprint_id?: string | null
  sprint_number?: number
  is_main_sprint?: boolean
}

export type SpaceFolderInsert = {
  user_id: string
  space_id: string
  name: string
  description?: string | null
  color?: string | null
  icon?: string | null
  position?: number
  archived?: boolean
}

export type SpaceTaskSubtaskInsert = {
  user_id: string
  space_id: string
  parent_task_id: string
  title: string
  description?: string | null
  completed?: boolean
  position?: number
  assigned_to?: string | null
  assigned_to_virtual?: string | null
  due_date?: string | null
}

export type SpaceTaskChecklistItemInsert = {
  user_id: string
  space_id: string
  task_id: string
  text: string
  completed?: boolean
  position?: number
}

export type SpaceTaskCommentInsert = {
  user_id: string
  space_id: string
  task_id: string
  parent_comment_id?: string | null
  content: string
  content_type?: "html" | "markdown" | "text"
}

export type SpaceTaskNoteInsert = {
  user_id: string
  space_id: string
  task_id: string
  title: string
  content?: string | null
  content_type?: "html" | "markdown" | "text"
}

export type SpaceTaskAttachmentInsert = {
  user_id: string
  space_id: string
  task_id: string
  filename: string
  original_filename: string
  file_path: string
  file_size: number
  mime_type: string
  description?: string | null
  thumbnail_path?: string | null
}

export type SpaceConversationInsert = {
  user_id: string
  space_id: string
  folder_id?: string | null
  sprint_id?: string | null
  task_id?: string | null
  note_id?: string | null
  name: string
  description?: string | null
  context_type: ConversationContextType
  auto_include_hierarchy?: boolean
  auto_include_tasks?: boolean
  auto_include_notes?: boolean
  auto_include_attachments?: boolean
  model?: string
  temperature?: number
  max_tokens?: number
}

export type SpaceConversationMessageInsert = {
  conversation_id: string
  request_user_id: string
  user_id?: string | null
  role: "user" | "assistant" | "system"
  content: string
  content_type?: "text" | "markdown" | "html"
  context_snapshot?: Json | null
  annotation?: Json | null
  model?: string
  tokens_used?: number
  processing_time_ms?: number
}

export type SpaceEventInsert = {
  user_id: string
  space_id: string
  title: string
  description?: string | null
  location?: string | null
  start_time: string
  end_time: string
  all_day?: boolean
  timezone?: string
  is_recurring?: boolean
  recurrence_rule?: string | null
  recurrence_end_date?: string | null
  task_id?: string | null
}

export type SpaceWhiteboardInsert = {
  user_id: string
  space_id: string
  name: string
  description?: string | null
  canvas_data?: WhiteboardCanvasData | null
  width?: number
  height?: number
  background_color?: string
  grid_enabled?: boolean
  sharing?: "space" | "private" | "public"
}

export type SpaceViewInsert = {
  space_id: string
  user_id: string
  name: string
  view_type: SpaceViewType
  config?: any
  is_default?: boolean
  is_shared?: boolean
}

// ============================================================================
// UPDATE TYPES
// ============================================================================

export type SpaceUpdate = Partial<Omit<SpaceInsert, "user_id" | "workspace_id">>

export type SpaceMemberUpdate = Partial<
  Omit<SpaceMemberInsert, "space_id" | "user_id">
>

export type SpaceNoteUpdate = Partial<
  Omit<SpaceNoteInsert, "user_id" | "space_id">
> & {
  archived_at?: string | null
  last_edited_by?: string | null
  space_id?: string
  updated_at?: string | null
}

export type SpaceDesignUpdate = Partial<
  Omit<SpaceDesignInsert, "user_id" | "space_id">
> & {
  archived_at?: string | null
  last_edited_by?: string | null
}

export type SpaceSheetUpdate = Partial<
  Omit<SpaceSheetInsert, "user_id" | "space_id">
> & {
  archived_at?: string | null
  last_edited_by?: string | null
}

export type SpaceTaskUpdate = Partial<
  Omit<SpaceTaskInsert, "user_id" | "space_id">
> & {
  archived_at?: string | null
}

export type SpaceSprintUpdate = Partial<
  Omit<SpaceSprintInsert, "user_id" | "space_id">
> & {
  archived_at?: string | null
}

export type SpaceFolderUpdate = Partial<
  Omit<SpaceFolderInsert, "user_id" | "space_id">
>

export type SpaceTaskSubtaskUpdate = Partial<
  Omit<SpaceTaskSubtaskInsert, "user_id" | "space_id" | "parent_task_id">
>

export type SpaceTaskChecklistItemUpdate = Partial<
  Omit<SpaceTaskChecklistItemInsert, "user_id" | "space_id" | "task_id">
>

export type SpaceTaskCommentUpdate = Partial<
  Omit<SpaceTaskCommentInsert, "user_id" | "space_id" | "task_id">
>

export type SpaceTaskNoteUpdate = Partial<
  Omit<SpaceTaskNoteInsert, "user_id" | "space_id" | "task_id">
>

export type SpaceTaskAttachmentUpdate = Partial<
  Omit<SpaceTaskAttachmentInsert, "user_id" | "space_id" | "task_id" | "filename" | "file_path" | "file_size" | "mime_type">
>

export type SpaceConversationUpdate = Partial<
  Omit<SpaceConversationInsert, "user_id" | "space_id">
>

export type SpaceConversationMessageUpdate = Partial<
  Omit<
    SpaceConversationMessageInsert,
    "conversation_id" | "request_user_id" | "user_id"
  >
>

export type SpaceEventUpdate = Partial<
  Omit<SpaceEventInsert, "user_id" | "space_id">
>

export type SpaceWhiteboardUpdate = Partial<
  Omit<SpaceWhiteboardInsert, "user_id" | "space_id">
>

export type SpaceViewUpdate = Partial<
  Omit<SpaceViewInsert, "space_id" | "user_id">
>

// ============================================================================
// EXTENDED TYPES (with related data)
// ============================================================================

export interface SpaceWithMembers extends Space {
  members?: SpaceMember[]
  member_count?: number
}

export interface SpaceWithMemberInfo extends Space {
  user_role?: SpaceRole
  is_member?: boolean
}

export interface TaskWithAssignee extends SpaceTask {
  assignee_ids?: string[]
  assignee?: SpaceTaskAssigneeProfile | null
  assignees?: SpaceTaskAssigneeProfile[]
  assigner?: SpaceTaskAssigneeProfile | null
}

export interface TaskWithSubtasks extends SpaceTask {
  subtasks?: SpaceTask[]
  subtask_count?: number
}

export interface EventWithAttendees extends SpaceEvent {
  attendees?: Array<
    SpaceEventAttendee & {
      user?: {
        id: string
        username: string
        image_url?: string
      }
    }
  >
  attendee_count?: number
}

export interface SprintWithStats extends SpaceSprint {
  total_tasks?: number
  completed_tasks?: number
  total_hours?: number
  completed_hours?: number
  sub_sprints?: SpaceSprint[]
  parent_sprint?: SpaceSprint | null
}

export interface ActivityWithUser extends SpaceActivity {
  user?: {
    id: string
    username: string
    image_url?: string
  }
}

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

export interface SpacePermissions {
  canView: boolean
  canComment: boolean
  canEdit: boolean
  canDelete: boolean
  canManageMembers: boolean
  canManageSettings: boolean
}

export const getSpacePermissions = (role?: SpaceRole): SpacePermissions => {
  if (!role) {
    return {
      canView: false,
      canComment: false,
      canEdit: false,
      canDelete: false,
      canManageMembers: false,
      canManageSettings: false
    }
  }

  const permissions: Record<SpaceRole, SpacePermissions> = {
    owner: {
      canView: true,
      canComment: true,
      canEdit: true,
      canDelete: true,
      canManageMembers: true,
      canManageSettings: true
    },
    admin: {
      canView: true,
      canComment: true,
      canEdit: true,
      canDelete: true,
      canManageMembers: true,
      canManageSettings: true
    },
    editor: {
      canView: true,
      canComment: true,
      canEdit: true,
      canDelete: false,
      canManageMembers: false,
      canManageSettings: false
    },
    viewer: {
      canView: true,
      canComment: false,
      canEdit: false,
      canDelete: false,
      canManageMembers: false,
      canManageSettings: false
    },
    commenter: {
      canView: true,
      canComment: true,
      canEdit: false,
      canDelete: false,
      canManageMembers: false,
      canManageSettings: false
    }
  }

  return permissions[role]
}

// ============================================================================
// FILTER & SORT TYPES
// ============================================================================

export interface TaskFilters {
  status?: TaskStatus[]
  priority?: TaskPriority[]
  assignee_ids?: string[]
  assigned_to?: string[]
  tags?: string[]
  sprint_id?: string | null
  has_due_date?: boolean
  include_overdue?: boolean
  is_overdue?: boolean
  planner_range_end?: string
  planner_range_start?: string
  search?: string
}

export interface TaskSort {
  field:
    | "position"
    | "created_at"
    | "updated_at"
    | "due_date"
    | "priority"
    | "title"
  direction: "asc" | "desc"
}

export interface NoteFilters {
  search?: string
  template?: boolean
  locked?: boolean
}

export interface DesignFilters {
  search?: string
  locked?: boolean
  folder_id?: string
  sprint_id?: string
}

export interface SheetFilters {
  search?: string
  locked?: boolean
  folder_id?: string
  sprint_id?: string
}

export interface SprintFilters {
  status?: SprintStatus[]
  date_range?: {
    start?: string
    end?: string
  }
}

// ============================================================================
// VIEW CONFIG TYPES
// ============================================================================

export interface ViewConfig {
  filters?: TaskFilters | NoteFilters | DesignFilters | SheetFilters | SprintFilters
  sort?: TaskSort
  group_by?:
    | "status"
    | "priority"
    | "assigned_to"
    | "sprint"
    | "due_date"
    | "none"
  show_completed?: boolean
  show_archived?: boolean
}

export type SpaceDesignSceneRecord = Record<string, unknown>

export type SpaceDesignSceneObject = SpaceDesignSceneRecord & {
  id: string
}

export type SpaceDesignScenePage = SpaceDesignSceneRecord & {
  id: string
  name: string
  objects: SpaceDesignSceneObject[]
}

export type SpaceDesignScene = SpaceDesignSceneRecord & {
  version: number
  activePageId: string
  pages: SpaceDesignScenePage[]
  metadata?: SpaceDesignSceneRecord
}

export type SpaceDesignSceneMutationType =
  | "setMetadata"
  | "createPage"
  | "updatePage"
  | "deletePage"
  | "setActivePage"
  | "upsertObject"
  | "updateObject"
  | "deleteObject"
  | "moveObject"

export type SpaceDesignSceneMutationPatch = SpaceDesignSceneRecord

export type SpaceDesignScenePageTarget = {
  pageId?: string
  pageName?: string
}

export type SpaceDesignSetMetadataMutation = {
  type: "setMetadata"
  patch: SpaceDesignSceneMutationPatch
}

export type SpaceDesignCreatePageMutation = SpaceDesignScenePageTarget & {
  type: "createPage"
  page?: SpaceDesignSceneRecord | null
  position?: number
  setActive?: boolean
}

export type SpaceDesignUpdatePageMutation = SpaceDesignScenePageTarget & {
  type: "updatePage"
  patch: SpaceDesignSceneMutationPatch
}

export type SpaceDesignDeletePageMutation = SpaceDesignScenePageTarget & {
  type: "deletePage"
}

export type SpaceDesignSetActivePageMutation = SpaceDesignScenePageTarget & {
  type: "setActivePage"
}

export type SpaceDesignUpsertObjectMutation = SpaceDesignScenePageTarget & {
  type: "upsertObject"
  object?: SpaceDesignSceneRecord | null
  objectId?: string
  position?: number
}

export type SpaceDesignUpdateObjectMutation = SpaceDesignScenePageTarget & {
  type: "updateObject"
  objectId: string
  patch: SpaceDesignSceneMutationPatch
}

export type SpaceDesignDeleteObjectMutation = SpaceDesignScenePageTarget & {
  type: "deleteObject"
  objectId: string
}

export type SpaceDesignMoveObjectMutation = SpaceDesignScenePageTarget & {
  type: "moveObject"
  objectId: string
  toPageId?: string
  toPageName?: string
  beforeObjectId?: string
  afterObjectId?: string
  position?: number
}

export type SpaceDesignSceneMutationOperation =
  | SpaceDesignSetMetadataMutation
  | SpaceDesignCreatePageMutation
  | SpaceDesignUpdatePageMutation
  | SpaceDesignDeletePageMutation
  | SpaceDesignSetActivePageMutation
  | SpaceDesignUpsertObjectMutation
  | SpaceDesignUpdateObjectMutation
  | SpaceDesignDeleteObjectMutation
  | SpaceDesignMoveObjectMutation

// ============================================================================
// CANVAS/WHITEBOARD TYPES
// ============================================================================

export const WHITEBOARD_COLOR_OPTIONS = [
  "black",
  "grey",
  "light-violet",
  "violet",
  "blue",
  "light-blue",
  "yellow",
  "orange",
  "green",
  "light-green",
  "light-red",
  "red",
  "white"
] as const

export const WHITEBOARD_FILL_STYLE_OPTIONS = [
  "none",
  "semi",
  "solid",
  "pattern",
  "fill"
] as const

export const WHITEBOARD_DASH_STYLE_OPTIONS = [
  "draw",
  "solid",
  "dashed",
  "dotted"
] as const

export const WHITEBOARD_SIZE_OPTIONS = ["s", "m", "l", "xl"] as const

export const WHITEBOARD_FONT_OPTIONS = [
  "draw",
  "sans",
  "serif",
  "mono"
] as const

export const WHITEBOARD_SHAPE_TYPES = [
  "rectangle",
  "circle",
  "diamond",
  "triangle",
  "cloud",
  "note",
  "text",
  "line",
  "arrow",
  "image"
] as const

export const WHITEBOARD_CONNECTION_TYPES = [
  "straight",
  "curved",
  "stepped"
] as const

export const WHITEBOARD_ANCHOR_OPTIONS = [
  "center",
  "top",
  "right",
  "bottom",
  "left"
] as const

export const WHITEBOARD_ARROWHEAD_OPTIONS = [
  "none",
  "arrow",
  "triangle",
  "square",
  "dot",
  "pipe",
  "diamond",
  "inverted",
  "bar"
] as const

export type WhiteboardColor = (typeof WHITEBOARD_COLOR_OPTIONS)[number]
export type WhiteboardFillStyle = (typeof WHITEBOARD_FILL_STYLE_OPTIONS)[number]
export type WhiteboardDashStyle = (typeof WHITEBOARD_DASH_STYLE_OPTIONS)[number]
export type WhiteboardSize = (typeof WHITEBOARD_SIZE_OPTIONS)[number]
export type WhiteboardFont = (typeof WHITEBOARD_FONT_OPTIONS)[number]
export type WhiteboardShapeType = (typeof WHITEBOARD_SHAPE_TYPES)[number]
export type WhiteboardConnectionType =
  (typeof WHITEBOARD_CONNECTION_TYPES)[number]
export type WhiteboardAnchor = (typeof WHITEBOARD_ANCHOR_OPTIONS)[number]
export type WhiteboardArrowhead = (typeof WHITEBOARD_ARROWHEAD_OPTIONS)[number]

export interface WhiteboardPoint {
  x: number
  y: number
}

export interface WhiteboardShape {
  id: string
  type: WhiteboardShapeType
  x: number
  y: number
  width?: number
  height?: number
  rotation?: number
  opacity?: number
  color?: WhiteboardColor
  fillStyle?: WhiteboardFillStyle
  dash?: WhiteboardDashStyle
  size?: WhiteboardSize
  font?: WhiteboardFont
  textAlign?: "start" | "middle" | "end"
  align?: "start" | "middle" | "end"
  verticalAlign?: "start" | "middle" | "end"
  url?: string
  fill?: string
  stroke?: string
  strokeWidth?: number
  text?: string
  fontSize?: number
  fontFamily?: string
  points?: WhiteboardPoint[]
  start?: WhiteboardPoint
  end?: WhiteboardPoint
  bend?: number
  arrowheadStart?: WhiteboardArrowhead
  arrowheadEnd?: WhiteboardArrowhead
  src?: string // For images
  locked?: boolean
  z_index?: number
}

export interface WhiteboardConnection {
  id: string
  from_shape_id: string
  to_shape_id: string
  type: WhiteboardConnectionType
  from_anchor?: WhiteboardAnchor
  to_anchor?: WhiteboardAnchor
  color?: WhiteboardColor
  dash?: WhiteboardDashStyle
  size?: WhiteboardSize
  text?: string
  bend?: number
  arrowheadStart?: WhiteboardArrowhead
  arrowheadEnd?: WhiteboardArrowhead
  stroke?: string
  strokeWidth?: number
}

export type WhiteboardStoreSnapshot = TLStoreSnapshot

export interface WhiteboardCanvasData {
  shapes: Record<string, WhiteboardShape>
  connections: Record<string, WhiteboardConnection>
  version: number
  last_modified: string
  shape_count?: number
  connection_count?: number
  // Canonical tldraw document snapshot, kept alongside the legacy
  // Losi shape/connection maps for compatibility.
  snapshot?: WhiteboardStoreSnapshot | null
}

// ============================================================================
// STATS & ANALYTICS
// ============================================================================

export interface SpaceStats {
  total_tasks: number
  completed_tasks: number
  overdue_tasks: number
  total_notes: number
  total_events: number
  total_members: number
  activity_count_7d: number
  completion_rate: number
}

export interface TaskStats {
  by_status: Record<string, number>
  by_priority: Record<TaskPriority, number>
  by_assignee: Record<string, number>
  overdue_count: number
  due_today_count: number
  due_this_week_count: number
}

export interface SprintVelocity {
  sprint_id: string
  sprint_name: string
  planned_points: number
  completed_points: number
  velocity: number
}

// ============================================================================
// REAL-TIME TYPES
// ============================================================================

export interface SpacePresence {
  user_id: string
  username: string
  image_url?: string
  cursor?: {
    x: number
    y: number
  }
  viewing?: {
    type: SpaceItemType
    id: string
  }
  last_seen: string
}

export interface RealtimeTaskUpdate {
  task_id: string
  updates: SpaceTaskUpdate
  user_id: string
  timestamp: string
}

export interface RealtimeWhiteboardUpdate {
  whiteboard_id: string
  operation: "add" | "update" | "delete"
  shape_id: string
  data: WhiteboardShape | null
  user_id: string
  timestamp: string
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export interface SpaceContextType {
  selectedSpace: Space | null
  setSelectedSpace: (space: Space | null) => void
  spaces: Space[]
  setSpaces: (spaces: Space[]) => void
  spaceTasks: SpaceTask[]
  setSpaceTasks: (tasks: SpaceTask[]) => void
  spaceSheets: SpaceSheet[]
  setSpaceSheets: (sheets: SpaceSheet[]) => void
  spaceNotes: SpaceNote[]
  setSpaceNotes: (notes: SpaceNote[]) => void
  spaceSprints: SpaceSprint[]
  setSpaceSprints: (sprints: SpaceSprint[]) => void
  spaceEvents: SpaceEvent[]
  setSpaceEvents: (events: SpaceEvent[]) => void
  spaceMembers: SpaceMember[]
  setSpaceMembers: (members: SpaceMember[]) => void
  isLoadingSpaces: boolean
  setIsLoadingSpaces: (loading: boolean) => void
}

// Export default constants
export const SPACE_ROLES: SpaceRole[] = [
  "owner",
  "admin",
  "editor",
  "viewer",
  "commenter"
]

export const TASK_STATUSES: BuiltInTaskStatus[] = [
  "todo",
  "in_progress",
  "done",
  "blocked",
  "cancelled"
]

export const TASK_PRIORITIES: TaskPriority[] = ["low", "medium", "high", "urgent"]

export const SPRINT_STATUSES: SprintStatus[] = [
  "planned",
  "active",
  "completed",
  "cancelled"
]

export const VIEW_TYPES: SpaceViewType[] = [
  "list",
  "board",
  "calendar",
  "timeline",
  "table",
  "gantt"
]

export const normalizeSpaceViewType = (
  view?: string | null
): SpaceViewType => {
  switch (view) {
    case "board":
    case "calendar":
    case "timeline":
    case "table":
    case "gantt":
    case "list":
      return view
    case "kanban":
      return "board"
    default:
      return "list"
  }
}
